import { SyntheticComplianceStatus } from '@commenda/types/src/compliance-calendar/compliances/types';
import { ObligationRulesCategory } from '@prisma/client';

// Palette colors that don't have token equivalents yet — kept as hex constants
// here so the calendar files don't sprinkle raw hexes across the directory.
// Move to packages/tokens when a wider compliance UI uses them.

export const CATEGORY_COLOR: Partial<Record<ObligationRulesCategory, string>> = {
  DIRECT_TAX: '#f4b548',
  INDIRECT_TAX: '#7cb8f6',
  TRANSFER_PRICING: '#6172f3',
  ENTITY_COMPLIANCE: '#8c5eae',
  PAYROLL_AND_EMPLOYMENT: '#f38744',
  CUSTOMS_AND_TRADE: '#db688d',
};

export const CATEGORY_COLOR_FALLBACK = '#94a3b8';

// Tailwind-discoverable checkbox override classes per category. Tailwind's JIT
// needs to see literal class strings at build time, so we can't compose these
// dynamically from CATEGORY_COLOR.
export const CATEGORY_CHECKBOX_CLASS: Partial<Record<ObligationRulesCategory, string>> = {
  DIRECT_TAX:
    'data-[state=checked]:!bg-[#f4b548] data-[state=checked]:!border-[#f4b548]',
  INDIRECT_TAX:
    'data-[state=checked]:!bg-[#7cb8f6] data-[state=checked]:!border-[#7cb8f6]',
  TRANSFER_PRICING:
    'data-[state=checked]:!bg-[#6172f3] data-[state=checked]:!border-[#6172f3]',
  ENTITY_COMPLIANCE:
    'data-[state=checked]:!bg-[#8c5eae] data-[state=checked]:!border-[#8c5eae]',
  PAYROLL_AND_EMPLOYMENT:
    'data-[state=checked]:!bg-[#f38744] data-[state=checked]:!border-[#f38744]',
  CUSTOMS_AND_TRADE:
    'data-[state=checked]:!bg-[#db688d] data-[state=checked]:!border-[#db688d]',
};

export const CATEGORY_LABEL: Partial<Record<ObligationRulesCategory, string>> = {
  DIRECT_TAX: 'Direct Tax',
  INDIRECT_TAX: 'Indirect Tax',
  TRANSFER_PRICING: 'Transfer Pricing',
  ENTITY_COMPLIANCE: 'Entity Compliance',
  PAYROLL_AND_EMPLOYMENT: 'Payroll & Employment',
  CUSTOMS_AND_TRADE: 'Customs and Trade',
  OTHER: 'Other',
};

export const STATUS_LABEL: Partial<Record<SyntheticComplianceStatus, string>> = {
  [SyntheticComplianceStatus.UPCOMING]: 'Not started',
  [SyntheticComplianceStatus.FILED]: 'Filed',
  [SyntheticComplianceStatus.COMPLETED]: 'Completed',
  [SyntheticComplianceStatus.ARCHIVED]: 'Archived',
  [SyntheticComplianceStatus.NOT_FILED]: 'Not filed',
  [SyntheticComplianceStatus.UNDER_FILING]: 'In progress',
  [SyntheticComplianceStatus.DUE_SOON]: 'Due soon',
  [SyntheticComplianceStatus.OVERDUE]: 'Overdue',
  [SyntheticComplianceStatus.REJECTED]: 'Rejected',
  [SyntheticComplianceStatus.UNKNOWN]: 'Unknown',
  [SyntheticComplianceStatus.EXTENDED]: 'Extended',
};

export type ComplianceBadgeVariant =
  | 'gray'
  | 'success'
  | 'blue'
  | 'warning'
  | 'error'
  | 'orange';

export const STATUS_BADGE_VARIANT: Partial<
  Record<SyntheticComplianceStatus, ComplianceBadgeVariant>
> = {
  [SyntheticComplianceStatus.UPCOMING]: 'gray',
  [SyntheticComplianceStatus.FILED]: 'success',
  [SyntheticComplianceStatus.COMPLETED]: 'success',
  [SyntheticComplianceStatus.ARCHIVED]: 'gray',
  [SyntheticComplianceStatus.NOT_FILED]: 'gray',
  [SyntheticComplianceStatus.UNDER_FILING]: 'blue',
  [SyntheticComplianceStatus.DUE_SOON]: 'warning',
  [SyntheticComplianceStatus.OVERDUE]: 'error',
  [SyntheticComplianceStatus.REJECTED]: 'error',
  [SyntheticComplianceStatus.UNKNOWN]: 'gray',
  [SyntheticComplianceStatus.EXTENDED]: 'orange',
};

// Bespoke palette colors used in misc calendar chrome that have no clean token
// equivalent today. Consolidated here so they're greppable in one place.
export const CALENDAR_TEXT_PRIMARY = '#14120f';
export const CALENDAR_TEXT_SECONDARY = '#57524d';
export const CALENDAR_TEXT_PLACEHOLDER = '#8f8b85';
export const CALENDAR_TEXT_OFF_RANGE = '#999';
export const CALENDAR_BG_WEEKEND = '#fdfdfc';
export const CALENDAR_BG_HOVER = '#f8f6f1';
export const CALENDAR_DIVIDER = '#edeceb';
export const CALENDAR_CARD_BORDER = '#dedbd4';
export const CALENDAR_SUBTITLE_DOT = '#c1bdb8';
export const CALENDAR_TODAY_ACCENT = '#c93829';
export const CALENDAR_CHECKBOX_INACTIVE = '#e8e5df';
